import { useState, type MouseEvent } from 'react';
import { palette } from '../theme';
import { usePortalPalette } from '../theme/usePortalPalette';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  Stack,
  Typography,
} from '../primitives';
import * as Icons from '../icons';
import { iconSize } from '@munifocus/tokens';

/** One row in the dropdown. Copy arrives already resolved to one language. */
export interface NotificationBellItem {
  id: string;
  title: string;
  body: string;
  /** ISO datetime — rendered as a short relative/absolute stamp by the caller. */
  timestamp: string;
  read: boolean;
  linkUrl?: string | null;
}

export interface NotificationBellProps {
  items: NotificationBellItem[];
  unread: number;
  loading?: boolean;
  /** Called when the dropdown opens — the moment to refetch. */
  onOpen?: () => void;
  onSelect?: (item: NotificationBellItem) => void;
  onMarkAllRead?: () => void;
  /** Renders the trigger for a dark bar (gov/admin top bars) instead of a light one. */
  onDark?: boolean;
  /** A link under the list, e.g. to a full inbox page. */
  footerAction?: { label: string; onClick: () => void };
  /** All copy is passed in so the app can localize through its own i18n context. */
  labels: {
    /** Accessible name when there is nothing unread. */
    title: string;
    /** Accessible name when there is — e.g. "Notifications, 3 unread". */
    titleUnread: string;
    heading: string;
    empty: string;
    markAllRead: string;
    loading: string;
  };
}

const MAX_BADGE = 9;

/**
 * The notification bell and its dropdown inbox, shared by the Officer and Admin
 * portals.
 *
 * Presentational only: it takes rows and callbacks, and every string is passed
 * in — `libs/ui` has no i18n context of its own, and the two portals localize
 * through their own. That also makes the component trivially testable without a
 * provider stack.
 *
 * The badge is a hand-rolled dot rather than MUI's `Badge` for the same reason
 * the Citizen Portal's is: the design system wants a flat teal pill with the
 * Manrope numerals, and overriding `Badge`'s anchor/transform machinery to get
 * there is more code than drawing it.
 *
 * Unread rows are marked by a teal dot and a heavier title, never by colour
 * alone — the same distinction has to survive a monochrome print of a case file
 * and a red-green colour deficiency.
 */
export function NotificationBell({
  items,
  unread,
  loading = false,
  onOpen,
  onSelect,
  onMarkAllRead,
  onDark = false,
  footerAction,
  labels,
}: NotificationBellProps) {
  const portal = usePortalPalette();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={unread > 0 ? labels.titleUnread : labels.title}
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{
          position: 'relative',
          color: onDark ? palette.text.onDarkStrong : palette.text.secondary,
        }}
      >
        {unread > 0 ? <Icons.BellRing size={iconSize.lg} /> : <Icons.Bell size={iconSize.lg} />}
        {unread > 0 && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 5,
              right: 5,
              minWidth: 16,
              height: 16,
              px: 0.5,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: "'Manrope', sans-serif",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
              color: palette.text.onDark,
              backgroundColor: portal.brand.accent,
            }}
          >
            {unread > MAX_BADGE ? `${MAX_BADGE}+` : unread}
          </Box>
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 480,
              border: `1px solid ${palette.border.line}`,
              borderRadius: 2,
            },
          },
          list: { sx: { py: 0 }, 'aria-label': labels.heading },
        }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {labels.heading}
          </Typography>
          {unread > 0 && onMarkAllRead && (
            <Button size="small" onClick={onMarkAllRead} sx={{ minWidth: 0 }}>
              {labels.markAllRead}
            </Button>
          )}
        </Stack>
        <Divider />

        {loading && (
          <Stack sx={{ alignItems: 'center', py: 3 }}>
            <CircularProgress size={20} aria-label={labels.loading} />
          </Stack>
        )}

        {!loading && items.length === 0 && (
          <Typography
            variant="body2"
            sx={{ px: 2, py: 3, color: palette.text.muted, textAlign: 'center' }}
          >
            {labels.empty}
          </Typography>
        )}

        {!loading &&
          items.map((item) => (
            <Box
              key={item.id}
              component="button"
              type="button"
              onClick={() => onSelect?.(item)}
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 0,
                cursor: 'pointer',
                backgroundColor: item.read ? 'transparent' : palette.background.parch,
                px: 2,
                py: 1.25,
                borderBottom: `1px solid ${palette.border.line}`,
                transition: 'background-color 200ms',
                '&:hover': { backgroundColor: palette.background.default },
              }}
            >
              <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start' }}>
                <Box
                  aria-hidden
                  sx={{
                    mt: 0.75,
                    width: 6,
                    height: 6,
                    flexShrink: 0,
                    borderRadius: '50%',
                    backgroundColor: item.read ? 'transparent' : portal.brand.accent,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: item.read ? 500 : 700, color: palette.text.primary }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                    {item.body}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: palette.text.muted, mt: 0.25 }}
                  >
                    {item.timestamp}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}

        {footerAction && (
          <Box sx={{ px: 1, py: 0.75 }}>
            <Button
              size="small"
              fullWidth
              onClick={() => {
                setAnchorEl(null);
                footerAction.onClick();
              }}
            >
              {footerAction.label}
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
}
